import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { motion, useReducedMotion } from "framer-motion";
import { AlertTriangle, TrendingUp, Users } from "lucide-react";
import { api } from "../../../api";
import { DESK } from "../../../constants/testIds";
import { useDeskData } from "../../../contexts/DeskDataContext";
import { formatCurrency } from "../../../lib/format";
import { fadeUp } from "../../../lib/motion";
import { DrillDown } from "../../../components/common/DrillDown";
import { StatCard } from "../../../components/common/StatCard";
import { ErrorState } from "../../../components/common/ErrorState";

function daysAgoIso(n) {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - n);
  return d.toISOString().slice(0, 10);
}

// UX-FD-03 §1 SCR-P02-80: real branch P&L (`GET /reports/pnl`, `pnl.view` — Manager-tier,
// genuinely different data from anything Front Desk can see) over a rolling 30-day window, plus
// leakage (denied entries) and live occupancy. "Occupancy trend" specifically has no history
// endpoint on this backend (only `/occupancy/live`) — shown honestly as a live snapshot rather
// than a fabricated historical chart; the revenue/profit daily trend IS real and does the
// "trend" job for the P&L half of this screen.
export default function BranchDashboard() {
  const { branchId } = useDeskData();
  const reducedMotion = useReducedMotion();
  const [drilldown, setDrilldown] = useState(null);

  const pnlQ = useQuery({
    queryKey: ["desk-mgr-pnl", branchId],
    queryFn: async () => (await api.get("/reports/pnl", { params: { branch_id: branchId, period_start: daysAgoIso(30), period_end: daysAgoIso(0) } })).data,
    enabled: !!branchId,
  });
  const leakageQ = useQuery({
    queryKey: ["desk-mgr-leakage", branchId],
    queryFn: async () => (await api.get("/metrics/leakage", { params: { branch_id: branchId } })).data,
    enabled: !!branchId,
  });
  const occupancyQ = useQuery({
    queryKey: ["desk-mgr-occupancy", branchId],
    queryFn: async () => (await api.get("/occupancy/live", { params: { branch_id: branchId } })).data,
    enabled: !!branchId,
  });

  const maxDaily = useMemo(() => Math.max(1, ...(pnlQ.data?.daily_trend || []).map((d) => Math.max(d.revenue, d.expense))), [pnlQ.data]);

  if (pnlQ.isError) return <ErrorState error={pnlQ.error} onRetry={() => pnlQ.refetch()} title="Couldn't load branch P&L" />;

  return (
    <div data-testid={DESK.mgrDashboardPage} className="space-y-6">
      <motion.div {...fadeUp(reducedMotion)}>
        <h1 className="text-2xl font-bold" style={{ color: "#141416" }}>Branch dashboard</h1>
        <p className="text-sm mt-0.5" style={{ color: "#68686E" }}>Last 30 days, real P&L for your branch only.</p>
      </motion.div>

      <motion.div {...fadeUp(reducedMotion, 0.03)} className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard testId={DESK.mgrRevenueStat} label="Revenue" value={pnlQ.data?.revenue ?? 0} formatValue={formatCurrency} loading={pnlQ.isLoading} index={0} onClick={() => setDrilldown("revenue")} />
        <StatCard testId={DESK.mgrExpenseStat} label="Expenses" value={pnlQ.data?.expense ?? 0} formatValue={formatCurrency} loading={pnlQ.isLoading} index={1} onClick={() => setDrilldown("expense")} />
        <StatCard testId={DESK.mgrProfitStat} label="Profit" value={pnlQ.data?.profit ?? 0} formatValue={formatCurrency} loading={pnlQ.isLoading} index={2} onClick={() => setDrilldown("profit")} />
        <StatCard testId={DESK.mgrLeakageStat} label="Denied entries" value={leakageQ.data?.denied_count ?? 0} loading={leakageQ.isLoading} index={3} onClick={() => setDrilldown("denied-entries")} />
      </motion.div>

      <motion.div {...fadeUp(reducedMotion, 0.06)} data-testid={DESK.mgrOccupancyStat} className="rounded-[14px] p-4 flex items-center gap-3" style={{ background: "#fff", border: "1px solid #EBEAE5" }}>
        <Users size={18} style={{ color: "#68686E" }} />
        <div>
          <p className="text-sm font-semibold" style={{ color: "#141416" }}>{occupancyQ.data?.count ?? "—"} currently in branch</p>
          <p className="text-xs" style={{ color: "#68686E" }}>Live snapshot — no occupancy history endpoint exists yet to chart a trend.</p>
        </div>
      </motion.div>

      <motion.div {...fadeUp(reducedMotion, 0.09)} className="rounded-[14px] p-5" style={{ background: "#fff", border: "1px solid #EBEAE5" }}>
        <p className="flex items-center gap-1.5 text-sm font-semibold mb-3" style={{ color: "#141416" }}><TrendingUp size={15} /> Revenue vs expense trend</p>
        {(!pnlQ.data?.daily_trend || pnlQ.data.daily_trend.length === 0) && (
          <p className="text-sm" style={{ color: "#68686E" }}>No ledger activity in this window yet.</p>
        )}
        <div className="flex items-end gap-1 h-32">
          {pnlQ.data?.daily_trend.map((d) => (
            <div key={d.date} title={`${d.date}: revenue ${formatCurrency(d.revenue)}, expense ${formatCurrency(d.expense)}`} className="flex-1 flex flex-col justify-end gap-0.5">
              <div style={{ height: `${(d.revenue / maxDaily) * 100}%`, background: "#D6202B", borderRadius: 2, minHeight: d.revenue ? 2 : 0 }} />
              <div style={{ height: `${(d.expense / maxDaily) * 100}%`, background: "#D9D6CE", borderRadius: 2, minHeight: d.expense ? 2 : 0 }} />
            </div>
          ))}
        </div>
        {pnlQ.data?.expense === 0 && pnlQ.data?.revenue === 0 && leakageQ.data?.denied_count > 0 && (
          <p className="flex items-center gap-1.5 text-xs mt-2" style={{ color: "var(--scarlet-neg)" }}><AlertTriangle size={12} /> High denied entries with no revenue — worth a look.</p>
        )}
      </motion.div>
      {drilldown && (
        <DrillDown
          metric={drilldown}
          branchId={branchId}
          period={{ period_start: daysAgoIso(30), period_end: daysAgoIso(0) }}
          onClose={() => setDrilldown(null)}
        />
      )}
    </div>
  );
}
